import { FC, useContext, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { useDueDateAttachmentApi } from "../../http/dueDateAttachments/api";
import {
  DueDateAttachment,
  RenameDueDateAttachment,
  ShowDueDateAttachmentToUser,
} from "../../http/dueDateAttachments/types";
import { isConflict } from "../../http/errors";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { formatTimeStamp, isEmbed, isImage, isMobile, isVideo } from "../../types/utils";
import SvgCancel from "../icons/Cancel";
import SvgCheckCircle from "../icons/CheckCircle";
import SvgEdit from "../icons/Edit";
import SvgVisible from "../icons/Visibility";
import SvgNotVisible from "../icons/VisibilityOff";
import { Check } from "../ui/Check";
import DownloadLink from "../ui/DownloadLink";
import FileIcon from "../ui/FileIcon";
import { Action, getDeleteAction, getDownloadAction, getPreviewAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import PreviewFileDialog from "../ui/PreviewFileDialog";
import Table from "../ui/Table";
import { FileToOverwrite, type TableUploadFileResult } from "../ui/TableFileUploadDialog";
import DeleteDueDateAttachmentDialog from "./DeleteDueDateAttachment";
import UploadDueDateAttachment from "./UploadDueDateAttachment";

const DUE_DATE_ATTACHMENTS_FIELDS = [
  "id",
  "name",
  "dueDateId",
  "uploader.fullName",
  "uploader.id",
  "editor.fullName",
  "editor.id",
  "uploadedAt",
  "modifiedAt",
  "isVisibleToUser",
] as const;
type DueDateAttachmentsField = (typeof DUE_DATE_ATTACHMENTS_FIELDS)[number];
type DueDateAttachmentSearch = NestedPick<DueDateAttachment, DueDateAttachmentsField>;

type DueDateAttachmentsProps = {
  dueDateId: string;
  carId: string;
  setIsDragging?: (isDragging: boolean) => void;
};

const DueDateAttachments: FC<DueDateAttachmentsProps> = ({ dueDateId, carId, setIsDragging }) => {
  const i18n = useI18n();
  const dueDateAttachmentsApi = useDueDateAttachmentApi();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [itemToDelete, setItemToDelete] = useState<DueDateAttachmentSearch>();
  const [documentIdToRename, setDocumentIdToRename] = useState<string>("");
  const [itemToPreview, setItemToPreview] = useState<{ name: string; url: string }>();
  const [fileToOverwrite, setFileToOverwrite] = useState<FileToOverwrite>();
  const [isOverwritingFile, setIsOverwritingFile] = useState(false);
  const api = useApi();
  const canUploadDocuments = userSettings.permissions.dueDates.insert;

  const onSubmit = async (id: string, name: string, callback: () => void): Promise<void> => {
    const body: RenameDueDateAttachment = {
      name: name,
    };

    try {
      await dueDateAttachmentsApi.renameDueDateAttachment(id, body, dueDateId);
      callback();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  const ATTACHMENTS_FIELDS = ["id"] as const;

  const query: PaginationQueryWithFields<DueDateAttachment> = {
    ...filters.state,
    fields: ATTACHMENTS_FIELDS,
  };

  const result = dueDateAttachmentsApi.useAdminDueDateAttachments(query, aborter.current.signal, dueDateId);
  const [data] = useDataInitializer(result.data, result.error);

  const uploadDueDateAttachment = async (file: File): Promise<TableUploadFileResult> => {
    try {
      await dueDateAttachmentsApi.insertDueDateAttachment(file, false, dueDateId);
    } catch (error) {
      if (!isConflict(error)) {
        throw error;
      }

      return new Promise<TableUploadFileResult>((resolve, reject) => {
        setFileToOverwrite({
          file,
          onCancel: () => {
            setFileToOverwrite(undefined);
            resolve("skipped");
          },
          onOverwrite: () => {
            setIsOverwritingFile(true);
            dueDateAttachmentsApi
              .insertDueDateAttachment(file, true, dueDateId)
              .then(() => {
                setFileToOverwrite(undefined);
                resolve("success");
              })
              .catch((error) => {
                setFileToOverwrite(undefined);
                reject(error);
              })
              .finally(() => setIsOverwritingFile(false));
          },
        });
      });
    }
  };

  const downloadAttachment = (item: DueDateAttachment) =>
    api
      .downloadFile(`/api/admin/due_date_attachments/${item.id}` + `?t=${new Date().getTime()}`, item.name)
      .catch((err) => dispatch(showErrorNotification(i18n, err)));

  const onPreviewClick = async (item: DueDateAttachment) => {
    if (!isMobile && (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))) {
      const docUrl = await api.createObjectURL(
        `/api/admin/due_date_attachments/${item.id}` + `?t=${new Date().getTime()}`,
        true,
      );
      setItemToPreview({ name: item.name, url: docUrl });
      return;
    }
    downloadAttachment(item);
  };

  const getRowActions = (item: DueDateAttachment) => {
    const actions: Action[] = [];

    if (userSettings.permissions.dueDates.update) {
      actions.push({
        title: item.isVisibleToUser ? i18n.t.common.hideToUser : i18n.t.common.showToUser,
        onClick: async () => {
          const body: ShowDueDateAttachmentToUser = { IsVisibleToUser: !item.isVisibleToUser };
          await dueDateAttachmentsApi.updateDueDateAttachmentVisibleToUser(item.id, body, item.dueDateId, carId);
        },
        icon: item.isVisibleToUser ? SvgNotVisible : SvgVisible,
      });
    }

    if (userSettings.permissions.dueDates.read) {
      actions.push(getDownloadAction(i18n, () => downloadAttachment(item)));
    }

    if (
      userSettings.permissions.dueDates.read &&
      !isMobile &&
      (isVideo(item.name) || isImage(item.name) || isEmbed(item.name))
    ) {
      actions.push(getPreviewAction(i18n, () => onPreviewClick(item)));
    }

    if (userSettings.permissions.dueDates.update) {
      actions.push({
        title: i18n.t.common.rename,
        onClick: () => setDocumentIdToRename(item.id),
        icon: SvgEdit,
      });
    }

    if (userSettings.permissions.dueDates.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), !!actions.length));
    }

    return actions;
  };

  return (
    <>
      <Table<DueDateAttachment>
        filters={filters}
        fileDrop={{
          enabled: canUploadDocuments,
          onUploadFile: uploadDueDateAttachment,
          isDraggingOn: setIsDragging,
        }}
        buttons={{
          main: canUploadDocuments && <UploadDueDateAttachment dueDateId={dueDateId} />,
        }}
        head={
          <>
            <th align="left" style={{ width: 1 }}></th>
            <th align="left">{i18n.t.common.name}</th>
            <th align="left">{i18n.t.common.uploadedBy}</th>
            <th align="left">{i18n.t.common.uploadedAt}</th>
            <th align="left">{i18n.t.common.modifiedBy}</th>
            <th align="left">{i18n.t.common.modifiedAt}</th>
            <th align="left">{i18n.t.common.visibleToUser}</th>
            <th></th>
          </>
        }
        renderRow={(item) => (
          <>
            <Table.IconTd>
              <FileIcon fileName={item.name} />
            </Table.IconTd>
            <Table.EditableCell
              defaultName={item.name}
              itemId={item.id}
              onSubmit={onSubmit}
              onCancel={() => setDocumentIdToRename("")}
              isEditing={item.id === documentIdToRename}
              disabled={!userSettings.permissions.dueDates.update}
            >
              <DownloadLink url={`/api/admin/due_date_attachments/${item.id}`} fileName={item.name}>
                {item.name}
              </DownloadLink>
            </Table.EditableCell>
            <td>{item.uploader.fullName}</td>
            <td>{formatTimeStamp(item.uploadedAt, i18n.language)}</td>
            <td>{item.editor?.fullName ?? ""}</td>
            <td>{!!item.editor ? formatTimeStamp(item.modifiedAt, i18n.language) : ""}</td>
            <td align="center">
              <Check
                icon={item.isVisibleToUser ? SvgCheckCircle : SvgCancel}
                color={item.isVisibleToUser ? "green" : "red"}
                obscured={!item.isVisibleToUser}
              />
            </td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!fileToOverwrite && (
        <ModalDialog
          title={i18n.t.documents.alreadyPresent}
          isSubmitting={isOverwritingFile}
          onClose={() => {
            if (!isOverwritingFile) {
              fileToOverwrite.onCancel();
            }
          }}
          actions={[
            {
              title: i18n.t.common.cancel,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onCancel,
              type: "secondary",
            },
            {
              title: i18n.t.common.overwrite,
              disabled: isOverwritingFile,
              onClick: fileToOverwrite.onOverwrite,
            },
          ]}
        >
          <p>{fileToOverwrite.file.name}</p>
          {i18n.t.documents.questions.overwrite}
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <DeleteDueDateAttachmentDialog
          dueDateAttachmentId={itemToDelete.id}
          dueDateId={itemToDelete.dueDateId}
          onClose={() => setItemToDelete(undefined)}
        />
      )}

      {!!itemToPreview && (
        <PreviewFileDialog
          fileName={itemToPreview.name}
          onClose={() => setItemToPreview(undefined)}
          docUrl={itemToPreview.url}
        />
      )}
    </>
  );
};

export default DueDateAttachments;
